import * as React from "react";
import { Box, Flex, IconButton, Image, Spinner, Badge } from "@chakra-ui/react";
import { FiX, FiFile, FiFileText } from "react-icons/fi";
import { StorageService } from "@/client";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker?url";

// PDF.js Worker 설정
GlobalWorkerOptions.workerSrc = workerSrc;

interface UploadedFileDisplayProps {
  fileId: string;
  fileName: string;
  contentType: string;
  isImage: boolean;
  onRemove: () => void;
}

export const UploadedFileDisplay = ({
  fileId,
  fileName,
  contentType,
  isImage,
  onRemove,
}: UploadedFileDisplayProps) => {
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!fileId) {
      setImageUrl(null);
      return;
    }

    const loadPreview = async () => {
      setIsLoading(true);
      try {
        const objectName = `temp/${fileId}`;
        const response = await StorageService.getPresignedUrl({
          objectName,
        });
        const url = (response as { url: string }).url;

        // PDF 파일인 경우 첫 페이지를 썸네일로 렌더링
        if (contentType === "application/pdf") {
          try {
            const pdf = await getDocument(url).promise;
            const page = await pdf.getPage(1); // 첫 번째 페이지

            // 썸네일 크기에 맞게 스케일 계산 (120x120)
            const viewport = page.getViewport({ scale: 1.0 });
            const scale = 120 / Math.max(viewport.width, viewport.height);
            const scaledViewport = page.getViewport({ scale });

            // Canvas 생성
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (!context) {
              throw new Error("Canvas context not available");
            }

            canvas.width = scaledViewport.width;
            canvas.height = scaledViewport.height;

            // PDF 페이지를 canvas에 렌더링
            await page.render({
              canvasContext: context,
              viewport: scaledViewport,
            }).promise;

            // Canvas를 이미지 URL로 변환
            const thumbnailUrl = canvas.toDataURL("image/png");
            setImageUrl(thumbnailUrl);
          } catch (pdfError) {
            console.error("PDF 미리보기 생성 실패:", pdfError);
            // PDF 렌더링 실패 시 null로 설정하여 아이콘 표시
            setImageUrl(null);
          }
        } else if (isImage) {
          // 일반 이미지 파일
          setImageUrl(url);
        } else {
          // 이미지가 아닌 다른 파일 타입
          setImageUrl(null);
        }
      } catch (error) {
        console.error("파일 로드 실패:", error);
        setImageUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();
  }, [fileId, isImage, contentType]);

  const getFileTypeLabel = () => {
    if (contentType === "application/pdf") return "PDF";
    if (contentType.includes("word")) return "DOCX";
    if (contentType.includes("text")) return "TXT";
    if (contentType.includes("markdown")) return "MD";
    return "FILE";
  };

  return (
    <Box
      position="relative"
      w="120px"
      h="120px"
      borderRadius="md"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      bg="gray.50"
      _hover={{
        borderColor: "gray.300",
        boxShadow: "sm",
      }}
      transition="all 0.2s"
    >
      {isLoading ? (
        <Flex w="100%" h="100%" align="center" justify="center" bg="gray.100">
          <Spinner size="sm" />
        </Flex>
      ) : imageUrl ? (
        <Image
          src={imageUrl}
          alt={fileName}
          w="100%"
          h="100%"
          objectFit="cover"
          onError={() => setImageUrl(null)}
        />
      ) : (
        <Flex
          w="100%"
          h="100%"
          align="center"
          justify="center"
          bg="gray.100"
          flexDirection="column"
          gap={2}
          position="relative"
        >
          <Box fontSize="3xl" color="gray.400">
            {contentType === "application/pdf" ? <FiFileText /> : <FiFile />}
          </Box>
          <Badge
            position="absolute"
            bottom="8px"
            left="50%"
            transform="translateX(-50%)"
            fontSize="xs"
            colorScheme="gray"
            px={2}
            py={0.5}
            borderRadius="sm"
          >
            {getFileTypeLabel()}
          </Badge>
        </Flex>
      )}

      {/* 제거 버튼 */}
      <IconButton
        aria-label="파일 제거"
        size="xs"
        position="absolute"
        top="4px"
        right="4px"
        variant="solid"
        colorScheme="red"
        borderRadius="full"
        w="20px"
        h="20px"
        minW="20px"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        _hover={{
          bg: "red.600",
        }}
      >
        <FiX />
      </IconButton>

      {/* 파일명 툴팁 (긴 파일명의 경우) */}
      {fileName.length > 15 && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          bg="blackAlpha.700"
          color="white"
          fontSize="xs"
          px={1}
          py={0.5}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          title={fileName}
        >
          {fileName}
        </Box>
      )}
    </Box>
  );
};
